import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';

import { API_BASE_URL, ApiError, auth } from '../api/client';

const ICON_STROKE = {
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.7,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
};

/**
 * Session chip. Three outcomes:
 *  - `authDisabled` -> nothing rendered: local dev has no login to show.
 *  - authenticated  -> the avatar initial + e-mail + a Log out button.
 *  - 401            -> full-page redirect to /api/auth/login (explicit here
 *                      because /auth/me is deliberately excluded from the api
 *                      client's global 401 handler).
 *
 * The Log out button is an `<a>`, not a client-side `navigate()`: the session
 * must be destroyed on the server and the SPA fully re-mounted afterwards.
 */
export function UserChip() {
  const me = useQuery({
    queryKey: ['auth', 'me'],
    queryFn: ({ signal }) => auth.me(signal),
    staleTime: 60_000,
    retry: false,
  });

  useEffect(() => {
    if (me.error instanceof ApiError && me.error.status === 401) {
      window.location.assign(`${window.location.origin}${API_BASE_URL}/auth/login`);
    }
  }, [me.error]);

  const user = me.data?.user;
  if (me.data?.authDisabled || !user) return null;

  const initial = user.name[0]?.toUpperCase() ?? user.email[0]?.toUpperCase() ?? '?';

  return (
    <div className="user" title={user.email} data-testid="user-chip">
      <span className="user__avatar" aria-hidden="true">
        {initial}
      </span>
      <span className="user__meta">
        <span className="user__email">{user.email}</span>
      </span>
      {/* Full navigation on purpose — see the comment on `auth.logout`. */}
      <a className="user__logout" href={`${API_BASE_URL}/auth/logout`} aria-label="Log out" title="Log out">
        <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true">
          <path d="M14 4h4a1.5 1.5 0 0 1 1.5 1.5v13A1.5 1.5 0 0 1 18 20h-4" {...ICON_STROKE} />
          <path d="M9.5 8 5.5 12l4 4" {...ICON_STROKE} />
          <path d="M5.5 12h9" {...ICON_STROKE} />
        </svg>
        <span>Log out</span>
      </a>
    </div>
  );
}
